import React from "react";
import {
  commonStyles,
  colors,
  gradients,
  pageHeaderStyles,
} from "../AgenticPaymentAirdrop/styles";

const STATUS_OPTIONS = [
  { value: "pending_review", label: "Pending Review" },
  { value: "pending_airdrop", label: "Pending Airdrop" },
  { value: "prefund_eligible", label: "Prefund Eligible" },
  { value: "completed", label: "Completed" },
  { value: "claimed", label: "Claimed" },
  { value: "available", label: "Available" },
];

const STATUS_META = {
  pending_review: { label: "Pending Review", background: "#fff3cd", color: "#856404" },
  pending_airdrop: { label: "Pending Airdrop", background: "#e8daef", color: "#6c3483" },
  prefund_eligible: { label: "Prefund Eligible", background: "#d1ecf1", color: "#0c5460" },
  completed: { label: "Completed", background: "#d4edda", color: "#155724" },
  claimed: { label: "Claimed", background: "#e2e3e5", color: "#383d41" },
  available: { label: "Available", background: "#f0f4ff", color: "#2e3b61" },
};

const getClaimType = (wallet) => {
  if (wallet.isPremium && wallet.wave === 99) return { label: "Custom", bg: "#d5f5e3", color: "#1e8449" };
  if (wallet.isPremium) return { label: "Premium", bg: "#fdebd0", color: "#b7950b" };
  return { label: "Raffle", bg: "#ebf5fb", color: "#2471a3" };
};

const formatDateTime = (value) => {
  if (!value) return "N/A";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";

  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const buildActionUrl = (pageUrl, action, walletId) => {
  const base = pageUrl.replace(/\/$/, "");
  const url = new URL(`${base}/${action}`, window.location.origin);
  url.searchParams.set("wallet_id", walletId);
  return `${url.pathname}${url.search}`;
};

const buildFilterUrl = (pageUrl, statusFilter, waveFilter) => {
  const base = pageUrl.replace(/\/$/, "");
  const url = new URL(base, window.location.origin);
  if (statusFilter) url.searchParams.set("status", statusFilter);
  if (waveFilter) url.searchParams.set("wave", waveFilter);
  return `${url.pathname}${url.search}`;
};

const StatCard = ({ value, label, tone = "primary" }) => {
  const tones = {
    primary: gradients.primary,
    accent: gradients.accent,
    success: gradients.success,
    danger: gradients.danger,
    info: gradients.info,
  };

  return (
    <div
      style={{
        background: colors.white,
        border: `1px solid ${colors.border}`,
        borderRadius: "10px",
        boxShadow: "0 10px 24px rgba(46, 59, 97, 0.08)",
        overflow: "hidden",
      }}
    >
      <div style={{ background: tones[tone], height: "6px" }} />
      <div style={{ padding: "16px 18px" }}>
        <div style={{ fontSize: "28px", fontWeight: 700, color: colors.gray700, lineHeight: 1.1 }}>
          {value}
        </div>
        <div style={{ marginTop: "6px", fontSize: "13px", color: colors.gray600, fontWeight: 600 }}>
          {label}
        </div>
      </div>
    </div>
  );
};

const ClaimsActions = ({ wallet, pageUrl }) => {
  const actions = [];

  if (wallet.status === "pending_review") {
    actions.push(
      <a
        key="approve"
        href={buildActionUrl(pageUrl, "approve", wallet.id)}
        data-method="post"
        className="claim-action claim-action-approve"
      >
        Approve
      </a>,
    );
    actions.push(
      <a
        key="reject"
        href={buildActionUrl(pageUrl, "reject", wallet.id)}
        data-method="post"
        data-confirm="Are you sure you want to reject this claim?"
        className="claim-action claim-action-reject"
      >
        Reject
      </a>,
    );
  }

  if (wallet.status === "prefund_eligible" && wallet.payoutMethod === "visa") {
    actions.push(
      <a
        key="issue-card"
        href={buildActionUrl(pageUrl, "issue_card", wallet.id)}
        data-method="post"
        className="claim-action claim-action-success"
      >
        Prefund &amp; Issue Card
      </a>,
    );
  }

  if (wallet.status === "pending_airdrop") {
    actions.push(
      <a
        key="send-airdrop"
        href={buildActionUrl(pageUrl, "retry_airdrop", wallet.id)}
        data-method="post"
        data-confirm="Send the bounty airdrop for this wallet now? This disburses the held bounty and marks the claim completed."
        className="claim-action claim-action-approve"
      >
        Send Airdrop
      </a>,
    );
  }

  if (actions.length === 0) {
    return <span style={{ color: colors.gray500, fontSize: "13px" }}>No actions</span>;
  }

  return <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>{actions}</div>;
};

const GenesisClaimsReview = ({
  wallets = [],
  statusFilter = "pending_review",
  waveFilter = "",
  pageUrl = "/admin/genesis_claims_review",
  errorMessage = null,
}) => {
  const counts = wallets.reduce(
    (accumulator, wallet) => {
      const status = wallet.status || "unknown";
      accumulator.total += 1;
      accumulator[status] = (accumulator[status] || 0) + 1;
      return accumulator;
    },
    { total: 0 },
  );

  const activeStatusLabel =
    STATUS_OPTIONS.find((option) => option.value === statusFilter)?.label ||
    statusFilter
      .replace(/_/g, " ")
      .replace(/\b\w/g, (character) => character.toUpperCase());

  const navigateTo = (nextStatus) => {
    window.location.assign(buildFilterUrl(pageUrl, nextStatus, waveFilter));
  };

  return (
    <div className="liquidity-pool-airdrop" style={{ paddingBottom: "24px" }}>
      <div style={{ ...pageHeaderStyles.container, marginBottom: "20px", borderRadius: "12px" }}>
        <h1 style={pageHeaderStyles.title}>Genesis Claims Review</h1>
        <div style={{ ...pageHeaderStyles.infoBox, flexWrap: "wrap" }}>
          <span style={pageHeaderStyles.infoLabel}>Current View</span>
          <code style={pageHeaderStyles.infoValue}>{activeStatusLabel}</code>
          {waveFilter ? (
            <>
              <span style={pageHeaderStyles.infoLabel}>Wave</span>
              <code style={pageHeaderStyles.infoValue}>{waveFilter}</code>
            </>
          ) : null}
        </div>
      </div>

      {errorMessage ? (
        <div
          className="flash flash_error"
          style={{ marginBottom: "20px", padding: "14px 16px", borderRadius: "8px" }}
        >
          <span>{errorMessage}</span>
        </div>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "16px",
          marginBottom: "20px",
        }}
      >
        <StatCard value={counts.total || 0} label="Total Claims" tone="primary" />
        <StatCard value={counts.pending_review || 0} label="Pending Review" tone="accent" />
        <StatCard value={counts.prefund_eligible || 0} label="Prefund Eligible" tone="info" />
        <StatCard value={counts.completed || 0} label="Completed" tone="success" />
      </div>

      <div style={{ ...commonStyles.panel, marginBottom: "20px" }}>
        <div style={{ ...commonStyles.panelHeader, display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <span>Filter Claims</span>
          <span style={{ fontSize: "12px", opacity: 0.85 }}>Switching filters reloads the page with the same layout.</span>
        </div>
        <div style={{ padding: "18px 20px" }}>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            {STATUS_OPTIONS.map((option) => {
              const isActive = option.value === statusFilter;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => navigateTo(option.value)}
                  style={{
                    ...commonStyles.button,
                    ...(isActive ? commonStyles.buttonAccent : commonStyles.buttonOutline),
                    borderRadius: "999px",
                    padding: "10px 16px",
                    fontSize: "13px",
                    boxShadow: isActive ? "0 4px 10px rgba(229, 135, 52, 0.18)" : "none",
                  }}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div style={commonStyles.panel}>
        <div style={commonStyles.panelHeader}>Genesis Vanity Wallet Claims</div>
        {wallets.length === 0 ? (
          <div style={{ padding: "32px 24px", textAlign: "center", color: colors.gray600 }}>
            No claims found for {activeStatusLabel.toLowerCase()}.
          </div>
        ) : (
          <div className="index_table_container">
            <table className="index_table">
              <thead>
                <tr>
                  <th className="col">ID</th>
                  <th className="col">Vanity Name</th>
                  <th className="col">Type</th>
                  <th className="col">Bounty</th>
                  <th className="col">Tier</th>
                  <th className="col">Payout</th>
                  <th className="col">Tx Hash</th>
                  <th className="col">Claimed By</th>
                  <th className="col">Claim Date</th>
                  <th className="col">Status</th>
                  <th className="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {wallets.map((wallet) => {
                  const statusStyle = STATUS_META[wallet.status] || STATUS_META.available;
                  const claimType = getClaimType(wallet);
                  const claimedBy = wallet.user
                    ? `${wallet.user.full_name || "Unnamed user"} (${wallet.user.email})`
                    : wallet.claimedByUserId
                      ? `User ID: ${wallet.claimedByUserId}`
                      : "N/A";
                  const txHash = wallet.payoutTransactionHash || "";
                  const truncatedTx = txHash ? `${txHash.slice(0, 8)}...${txHash.slice(-6)}` : "N/A";

                  return (
                    <tr key={wallet.id}>
                      <td className="col">{wallet.id}</td>
                      <td className="col">{wallet.vanityName || "N/A"}</td>
                      <td className="col">
                        <span style={{ display: "inline-flex", alignItems: "center", padding: "3px 8px", borderRadius: "999px", background: claimType.bg, color: claimType.color, fontSize: "11px", fontWeight: 700 }}>
                          {claimType.label}
                        </span>
                      </td>
                      <td className="col">${wallet.bountyAmountUsdc || 0} USDb</td>
                      <td className="col">{wallet.bountyTier ? wallet.bountyTier.toString().replace(/\b\w/g, (character) => character.toUpperCase()) : "N/A"}</td>
                      <td className="col">{wallet.payoutMethod ? wallet.payoutMethod.toString().toUpperCase() : "N/A"}</td>
                      <td className="col" title={txHash}>
                        {txHash ? (
                          <a
                            href={`https://hashscan.io/mainnet/transaction/${txHash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: "11px", color: "#5b6abf", textDecoration: "underline", cursor: "pointer" }}
                          >
                            {truncatedTx}
                          </a>
                        ) : "N/A"}
                      </td>
                      <td className="col">{claimedBy}</td>
                      <td className="col">{formatDateTime(wallet.claimedAt)}</td>
                      <td className="col">
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "4px 10px",
                            borderRadius: "999px",
                            background: statusStyle.background,
                            color: statusStyle.color,
                            fontSize: "12px",
                            fontWeight: 700,
                            textTransform: "capitalize",
                          }}
                        >
                          {statusStyle.label}
                        </span>
                      </td>
                      <td className="col">
                        <ClaimsActions wallet={wallet} pageUrl={pageUrl} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default GenesisClaimsReview;
